import { useQuery } from '@tanstack/react-query'
import { parseAsInteger, useQueryState } from 'nuqs'
import { useEffect, useRef } from 'react'
import { useProjectListFilters, useTRPC } from '../../hooks'
import { isNotFound } from '../../utils'

const PAGE_SIZE = 10
const ALL_PAGE_SIZE = 1000
const PAGE_KEY = 'dashboard-page'

interface Properties {
  enabled: boolean
  fetchFullData?: boolean
}

export const useFilteredDashboards = ({
  enabled,
  fetchFullData = false,
}: Properties) => {
  const [page, setPage] = useQueryState(
    PAGE_KEY,
    parseAsInteger
      .withOptions({
        shallow: false,
      })
      .withDefault(1)
  )
  const { filters } = useProjectListFilters()
  const api = useTRPC()

  const paginatedDashboardQueryOptions =
    api.shared.dashboards.list.queryOptions(
      {
        page,
        page_size: PAGE_SIZE,
        data: filters,
      },
      {
        enabled,
      }
    )
  const allDashboardQueryOptions = api.shared.dashboards.list.queryOptions(
    {
      page_size: ALL_PAGE_SIZE,
      data: filters,
    },
    {
      enabled,
    }
  )

  const dashboardQueryOptions = fetchFullData
    ? allDashboardQueryOptions
    : paginatedDashboardQueryOptions

  const { data, isFetching, error } = useQuery(dashboardQueryOptions)

  const previousFiltersReference = useRef(filters)
  useEffect(() => {
    if (
      JSON.stringify(previousFiltersReference.current) !==
      JSON.stringify(filters)
    ) {
      void setPage(1)
      previousFiltersReference.current = filters
    }
  }, [filters, setPage])

  useEffect(() => {
    if (isNotFound(error) && page !== 1) {
      void setPage(1)
    }
  }, [error, page, setPage])

  return {
    page,
    setPage: (page: number) => {
      void setPage(page)
    },
    data,
    isFetching,
    queryKeysToInvalidate: [
      paginatedDashboardQueryOptions.queryKey,
      allDashboardQueryOptions.queryKey,
    ],
  }
}
